// src/components/categories/CategoryCard.jsx
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  CardActionArea,
  Box,
  Chip,
  Badge,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import { 
  Category as CategoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';
import { useTranslation } from 'react-i18next';

const CategoryCard = ({ category, onEdit, onDelete, getCategoryImageUrl, elevation = 1, sx = {} }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('common');
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  
  const handleClick = () => {
    // Child categories (have a parentId) redirect to search with the category filter
    if (category.parentId) {
      navigate(`/search?category=${category.id}`);
    } else {
      navigate(`/categories/${category.slug}`);
    }
  };
  
  // Image par défaut avec icône élégante
  const defaultImage = null;
  
  // Construire l'URL de l'image
  const imageUrl = getCategoryImageUrl ? getCategoryImageUrl(category.imageUrl) : category.imageUrl;
  
  return (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        elevation={elevation}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          },
          ...sx
        }}
      >
        <CardActionArea onClick={handleClick} sx={{ flexGrow: 1 }}>
          {/* Image ou icône de fallback */}
          {imageUrl ? (
            <CardMedia
              component="img"
              height="180"
              image={imageUrl}
              alt={category.name}
              sx={{ 
                objectFit: 'cover',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Fallback pour les images manquantes */}
          <Box
            sx={{
              height: 180,
              display: imageUrl ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              position: 'relative'
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <CategoryIcon 
                sx={{ 
                  fontSize: 32, 
                  color: theme.palette.primary.main 
                }} 
              />
            </Avatar>
          </Box>
          
          <CardContent sx={{ p: 2.5 }}>
            <Typography 
              gutterBottom 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                mb: 1
              }}
            >
              {getCategoryName(category)}
            </Typography>
            
            {category.description && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  mb: 2,
                  lineHeight: 1.4
                }}
              >
                {getCategoryDescription(category)}
              </Typography>
            )}
            
            {/* Badges et informations */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {category.children && category.children.length > 0 && (
                <Chip 
                  size="small" 
                  label={`${category.children.length} ${category.children.length > 1 ? t('categories.subCategoriesCountPlural') : t('categories.subCategoriesCount')}`}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '12px'
                  }}
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
        
        {/* Badge pour les nouvelles catégories */}
        {category.createdAt && new Date(category.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1
            }}
          >
            <Chip
              label={t('categories.new')}
              color="secondary"
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                fontWeight: 600,
                boxShadow: theme.shadows[2]
              }}
            />
          </Box>
        )}
      </Card>
    </motion.div>
  );
};

export default CategoryCard;